import {
  Site,
  type OpeningHours,
  type OpeningHoursDay,
  type OpeningHoursDays,
} from '@cinch-labs/sites-sdk'
import {
  type SiteInfo,
  SiteInfoSection,
} from './site-Info-section/site-info-section'
import { Map } from './site-location-map/site-location-map'
import styles from './site-location.module.css'

type SiteOpeningHours = Omit<OpeningHoursDays, 'department'>

const weekdays = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const

const formatOpeningHour = (hour: string) => {
  if (!hour) return ''
  if (hour === 'closed') return 'closed'
  // Validate  hour is in the expected 24-hour format (HHmm)
  if (!/^\d{4}$/.test(hour)) return hour

  // Convert 24-hour format to 12-hour format with am/pm
  // '0900' becomes '9am', '1300' becomes '1pm', '0000' becomes '12am'
  const HH = Number.parseInt(hour.slice(0, 2), 10)
  const mm = hour.slice(2)
  const displayHour = HH === 0 || HH === 12 ? 12 : HH > 12 ? HH - 12 : HH
  const minuteSuffix = mm === '00' ? '' : `:${mm}`

  return `${displayHour}${minuteSuffix}${HH < 12 ? 'am' : 'pm'}`
}

function getOpeningHours(
  OpeningHoursAttributeValue: OpeningHours | undefined,
): SiteOpeningHours | undefined {
  if (!OpeningHoursAttributeValue || OpeningHoursAttributeValue.length === 0) {
    return undefined
  }

  // Select opening hours for the 'cinch' department, or fallback to the first entry
  const selectedOpeningHours =
    OpeningHoursAttributeValue.find((openingHours) =>
      ['cinch'].includes(openingHours.department.toLowerCase()),
    ) ?? OpeningHoursAttributeValue[0]

  if (!selectedOpeningHours) {
    return undefined
  }

  // strip the 'department' property and format the opening hours for each day
  const { department: _department, ...openingHoursByDay } = selectedOpeningHours

  const formattedOpeningHours = {
    monday: { start: '', end: '' },
    tuesday: { start: '', end: '' },
    wednesday: { start: '', end: '' },
    thursday: { start: '', end: '' },
    friday: { start: '', end: '' },
    saturday: { start: '', end: '' },
    sunday: { start: '', end: '' },
  }

  for (const day of weekdays) {
    const dayValue = openingHoursByDay[day]

    if (!dayValue) {
      continue
    }

    formattedOpeningHours[day] = {
      start: formatOpeningHour((dayValue as OpeningHoursDay).start),
      end: formatOpeningHour((dayValue as OpeningHoursDay).end),
    }
  }

  return formattedOpeningHours
}

interface SiteLocationProps {
  phoneLabel: string
  emailLabel: string
  contactUsTelephoneNumber: string
  contactUsEmailAddress: string
  site?: Site
  slugBasedFallbackTitle: string
}

export const SiteLocation: React.FC<SiteLocationProps> = ({
  phoneLabel,
  emailLabel,
  contactUsTelephoneNumber,
  contactUsEmailAddress,
  site,
  slugBasedFallbackTitle,
}) => {
  if (!site) {
    return null
  }

  const displayName = site.getFeatureAttribute('display_name')
  const coordinates = site.getFeatureAttribute('coordinates')
  const address = site.getFeatureAttribute('address')
  const openingHoursAttribute = site.getFeatureAttribute('appointment_hours')
  const openingHours = getOpeningHours(openingHoursAttribute)
  const organisationalUnit = site.getFeatureAttribute('organisationalUnit')

  const siteInfo: SiteInfo = {
    displayName: displayName ? displayName : slugBasedFallbackTitle,
    coordinates,
    address,
    openingHours,
    organisationalUnit,
  }

  return (
    <div className={styles.container}>
      <SiteInfoSection
        site={siteInfo}
        contactUs={{
          phoneLabel: phoneLabel,
          emailLabel: emailLabel,
          telephoneNumber: contactUsTelephoneNumber,
          emailAddress: contactUsEmailAddress,
        }}
      />
      {coordinates && (
        <div className={styles.mapContainer}>
          <Map coordinates={coordinates} />
        </div>
      )}
    </div>
  )
}
